import { Form, Grid, Select, TextField } from '@approvalmax/ui/src/components';
import { memo, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { PaymentDetailsLegalEntityType } from 'shared/data/webApp/v2';

import { countries } from '../../EditCounterpartyPaymentDetailsPopupContent.constants';
import { checkIsFasterPaymentsMethod } from '../../EditCounterpartyPaymentDetailsPopupContent.helpers';
import { FormValues } from '../../EditCounterpartyPaymentDetailsPopupContent.types';
import { fieldsToPrefillForFirstPaymentDetails } from './ThirdStep.constants';
import { useRequiredFields } from './ThirdStep.hooks';
import { messages } from './ThirdStep.messages';
import { ThirdStepProps } from './ThirdStep.types';

export const ThirdStep = memo<ThirdStepProps>((props) => {
    const { requirements, contact, isFirstPaymentDetails } = props;

    const form = useFormContext<FormValues>();
    const requiredFields = useRequiredFields(requirements);

    const [paymentMethod, legalEntityType] = form.watch(['paymentMethod', 'legalEntity.legalEntityType']);

    useEffect(() => {
        if (!contact) {
            return;
        }

        const isFasterPaymentsAndCompany =
            checkIsFasterPaymentsMethod(paymentMethod) && legalEntityType === PaymentDetailsLegalEntityType.Company;

        if (isFasterPaymentsAndCompany) {
            const isDirty = form.getFieldState('bankAccount.bankAccountName').isDirty;

            if (!isDirty) {
                form.setValue('bankAccount.bankAccountName', contact.name, {
                    shouldDirty: true,
                });
            }
        }

        if (!isFasterPaymentsAndCompany && requiredFields.find((field) => field.name === 'legalEntity.companyName')) {
            const isDirty = form.getFieldState('legalEntity.companyName').isDirty;

            if (!isDirty) {
                form.setValue('legalEntity.companyName', contact.name, {
                    shouldDirty: true,
                });
            }
        }

        if (!isFirstPaymentDetails) {
            return;
        }

        fieldsToPrefillForFirstPaymentDetails.forEach(
            ({ name, isStaticField, xeroContactFinancialDetailsField, shouldIgnore }) => {
                const canPrefill = isStaticField || requiredFields.find((field) => field.name === name);
                const isIgnoredField = shouldIgnore?.(paymentMethod, legalEntityType) ?? false;

                if (canPrefill && !isIgnoredField) {
                    const isDirty = form.getFieldState(name).isDirty;

                    if (!isDirty) {
                        form.setValue(name, contact.financialDetails[xeroContactFinancialDetailsField], {
                            shouldDirty: true,
                        });
                    }
                }
            }
        );
    }, [form, requiredFields, contact, isFirstPaymentDetails, legalEntityType, paymentMethod]);

    return (
        <Form.Part>
            <Grid gridTemplateColumns='repeat(2, 1fr)' gap='16px'>
                <TextField
                    name='bankAccount.bankAccountName'
                    label={messages.bankAccountName}
                    placeholder={messages.inputPlaceholder}
                    required
                    minLength={2}
                    maxLength={200}
                    pattern={{
                        value: /^[^&<>/'"\\]{2,200}$/,
                        message: messages.bankAccountNameValidationError({
                            restrictedChars: '&, <, >, \', ", /, \\',
                        }),
                    }}
                />

                {requiredFields.map((field) => {
                    if (field.isHidden?.(paymentMethod, legalEntityType)) {
                        return null;
                    }

                    if (field.name === 'payerBankCountry') {
                        return (
                            <Select
                                key={field.name}
                                name={field.name}
                                label={field.title}
                                items={countries}
                                required
                                disabled={field.disabled}
                            />
                        );
                    }

                    return (
                        <TextField
                            key={field.name}
                            name={field.name}
                            label={field.title}
                            placeholder={messages.inputPlaceholder}
                            required
                            disabled={field.disabled}
                            pattern={
                                field.validationRegExp
                                    ? {
                                          value: field.validationRegExp,
                                          message: messages.validationErrorMessage,
                                      }
                                    : undefined
                            }
                        />
                    );
                })}
            </Grid>
        </Form.Part>
    );
});

ThirdStep.displayName = 'ThirdStep';
